import React, {useEffect, useState} from "react";
import {RouteComponentProps, useHistory, withRouter} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import isEmail from "validator/lib/isEmail";
import {useDispatch} from "useDispatch";
import {fetchOlmaPrize, postOlmaCollect} from "helpers/actions";
import {VoucherItem} from "types/entities/VoucherItem";
import UiButton, {UiButtonSize, UiButtonStyle} from "pages/Front/components/Ui/components/UiButton";
import {GamePrize} from "types/entities/GamePrize";

interface OlmaGameParams {
    params: string;
}

export interface OlmaGameResultResponse {
    prize_id: number;
    game_prize?: {
        id: number;
        guid: string;
        prize_number: number;
        driver_access_card_id: number | null;
        voucher_id: number;
        created_at: string;
        voucher: VoucherItem;
    };
}

type OlmaGameProps = RouteComponentProps<OlmaGameParams>;
type OlmaGameFormData = Record<string, string>;

const OLMA_GAME_TOKEN_SALT = process.env.REACT_APP_OLMA_GAME_TOKEN_SALT || "olma-2026-local-claim-salt";

const encodeBase64Url = (value: Uint8Array) => {
    let binary = "";

    value.forEach(byte => {
        binary += String.fromCharCode(byte);
    });

    return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const createFallbackHash = (value: string) => {
    let hash = 2166136261;

    for (let index = 0; index < value.length; index++) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }

    return (hash >>> 0).toString(36);
};

const createHash = async (value: string) => {
    if (!window.crypto?.subtle) {
        return createFallbackHash(value);
    }

    const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
    return encodeBase64Url(new Uint8Array(digest).slice(0, 16));
};

const decodeBase64Url = (value: string) => {
    try {
        const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
        const binary = window.atob(base64 + "=".repeat((4 - (base64.length % 4)) % 4));
        const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));

        return new TextDecoder().decode(bytes);
    } catch {
        return null;
    }
};

const isValidOlmaGameToken = async (token: string) => {
    const tokenParts = token.split(".");

    if (tokenParts.length !== 2 || !tokenParts[0] || !tokenParts[1]) {
        return false;
    }

    const payloadValue = decodeBase64Url(tokenParts[0]);
    if (!payloadValue) {
        return false;
    }

    try {
        const payload = JSON.parse(payloadValue);
        const hasValidPayload =
            payload &&
            payload.v === 1 &&
            Number.isFinite(payload.i) &&
            typeof payload.n === "string" &&
            payload.n.length > 0 &&
            (typeof payload.p === "string" || typeof payload.p === "number");

        if (!hasValidPayload) {
            return false;
        }

        const expectedHash = await createHash(tokenParts[0] + "." + OLMA_GAME_TOKEN_SALT);
        return expectedHash === tokenParts[1];
    } catch {
        return false;
    }
};

const OlmaGame = ({location, match}: OlmaGameProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [isPrizeLoading, setIsPrizeLoading] = useState(true);
    const [prizeId, setPrizeId] = useState<number | null>(null);
    const [prizeDetail, setPrizeDetail] = useState<GamePrize | null>(null);
    const [isInvalidToken, setIsInvalidToken] = useState(false);
    const history = useHistory();
    const routeParams = match.params.params;
    const queryParams = Array.from(new URLSearchParams(location.search).entries());

    const {
        handleSubmit,
        register,
        formState: {errors},
    } = useForm<OlmaGameFormData>();

    useEffect(() => {
        let isCancelled = false;

        const fetchPrize = async () => {
            setIsPrizeLoading(true);
            setPrizeId(null);
            setIsInvalidToken(false);

            try {
                if (!(await isValidOlmaGameToken(routeParams))) {
                    if (!isCancelled) {
                        setIsInvalidToken(true);
                    }
                    return;
                }

                const response = await dispatch(fetchOlmaPrize(routeParams));
                if (!isCancelled) {
                    setPrizeId(response.prize_id);
                    setPrizeDetail(response.prize);
                }
            } catch {
                if (!isCancelled) {
                    setIsInvalidToken(true);
                }
            } finally {
                if (!isCancelled) {
                    setIsPrizeLoading(false);
                }
            }
        };

        fetchPrize();

        return () => {
            isCancelled = true;
        };
    }, [dispatch, routeParams]);

    const onSubmit = async (data: OlmaGameFormData) => {
        if (isLoading) {
            return;
        }

        setIsLoading(true);
        setIsInvalidToken(false);

        try {
            if (prizeId === null) {
                setIsInvalidToken(true);
                return;
            }

            const response = (await dispatch(postOlmaCollect(data))) as unknown as OlmaGameResultResponse;
            console.log(response);
            const resultPath = response.game_prize ? "/olma/game/result/success" : "/olma/game/result";
            history.push(resultPath, {prizeDetail: response.game_prize ? prizeDetail : null, prizeId: response.prize_id});
        } catch (error) {
            if (typeof error === "object" && error !== null && "status" in error && error.status === 429) {
                history.push("/olma/game/result", {prizeDetail: null, prizeId});
            }
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <main className="OlmaGame flex min-h-[60vh] items-center justify-center px-4 py-12 z-20 relative">
            <div className="w-full max-w-xl rounded-2xl bg-white p-6 text-center shadow-lg sm:p-10 flex flex-col space-y-4 items-center justify-center">
                <picture className={"max-w-[230px]"}>
                    <img className={"w-full h-full object-contain"} src={"/img/arboarena.svg"} />
                </picture>
                <h1 className="text-3xl font-semibold font-zuume text-gray-900 sm:text-5xl">{t("Prize claim")}</h1>
                {prizeDetail && (
                    <div className={"prizeDetail rounded-xl flex flex-col bg-gray-200 p-6 w-full items-center"}>
                        <span className={"text-arbo-red font-zuume font-black text-4xl sm:text-6xl"}>
                            {prizeDetail.article.article_name as unknown as string}
                        </span>
                        {prizeDetail.text && <span className={"text-gray-800"}>{prizeDetail.text}</span>}
                    </div>
                )}
                {!isInvalidToken && <p className="mt-3 text-gray-700">{t("Please enter your e-mail address to retrieve your prize.")}</p>}
                {isInvalidToken && <p className="mt-4 text-sm text-red-600">{t("Invalid prize claim link.")}</p>}

                {!isInvalidToken && (
                    <form className="mt-8 text-left flex flex-col space-y-4 w-full" onSubmit={handleSubmit(onSubmit)}>
                        <input type="hidden" {...register("params")} defaultValue={routeParams} />
                        {queryParams.map(([key, value], index) => (
                            <input key={`${key}-${index}`} type="hidden" {...register(key as any)} defaultValue={value} />
                        ))}
                        <label className="block text-sm font-bold text-gray-800" htmlFor="olma-email">
                            <span>{t("Email address")}</span>
                        </label>
                        <input
                            autoComplete="email"
                            className="mt-2 block w-full rounded-md border-0 px-3.5 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-arbo-orange"
                            id="olma-email"
                            placeholder={t("Enter your e-mail address")}
                            type="email"
                            required
                            aria-invalid={errors.email ? "true" : "false"}
                            {...register("email", {
                                required: t("Email is required"),
                                setValueAs: value => (typeof value === "string" ? value.trim() : value),
                                validate: value =>
                                    isEmail(value, {
                                        allow_utf8_local_part: true,
                                        require_tld: true,
                                    }) || t("Invalid email format"),
                            })}
                        />
                        {errors.email && <p className="text-sm text-red-600">{errors.email.message}</p>}
                        <UiButton
                            className={"uppercase font-bold text-lg sm:text-xl "}
                            disabled={isLoading || isPrizeLoading || prizeId === null}
                            size={UiButtonSize.Medium}
                            style={UiButtonStyle.Green}
                            onClick={handleSubmit(onSubmit)}
                        >
                            <span>{t("Retrieve prize")}</span>
                        </UiButton>
                    </form>
                )}
            </div>
        </main>
    );
};

export default withRouter(OlmaGame);
